/**
 * The api.mixcloud.com calls Fadila makes. Unlike every other streaming
 * source in here there is no token and no client id: the read API is open,
 * and it answers CORS with whatever origin asked (measured from both
 * production and localhost - docs/MIXCLOUD.md section 1). So these
 * are plain fetches and there is no service context around them.
 *
 * Notes that are Mixcloud's, not ours:
 *
 * - Resources are addressed by *path*: `/DeepHouseAmsterdam/some-mix/` is a
 *   show and `/DeepHouseAmsterdam/` is a user. There are no ids. Any page on
 *   mixcloud.com becomes its API resource by swapping the host, which is
 *   what resolve() below does with a pasted link.
 * - Paging is `{ data, paging: { next } }`, where `next` is a whole URL, and
 *   there is no total anywhere. The QueryIterators leave `totalResults`
 *   undefined and the lists size themselves by what has loaded, as
 *   SoundCloud's do.
 * - There is no audio here. Playback is the widget, and the widget takes the
 *   same key these endpoints return (services/mixcloud/mixcloudWidget.ts).
 */

import MixcloudCategory, { MixcloudDiscoverSort } from '../../models/mixcloud/mixcloudCategory';
import MixcloudCloudcast from '../../models/mixcloud/mixcloudCloudcast';
import MixcloudPage from '../../models/mixcloud/mixcloudPage';
import MixcloudPlaylist from '../../models/mixcloud/mixcloudPlaylist';
import MixcloudUser from '../../models/mixcloud/mixcloudUser';
import QueryIterator from '../../models/queryIterator';

export const apiUrl = 'https://api.mixcloud.com';

/** The most any list endpoint returns per page. */
export const pageLimitMax = 100;

/**
 * A non-2xx from the API. `status` is what callers switch on; a show that is
 * gone answers 404 and a client asking too fast answers 403 with a
 * RateLimitException, which is not the same thing at all.
 */
export class MixcloudApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: any,
    /** Seconds Mixcloud asks us to wait, when it says. */
    public readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'MixcloudApiError';
  }
}

/**
 * Mixcloud's error bodies are `{ error: { type, message, retry_after? } }`.
 * Take the message when there is one, and say something useful when there
 * is not.
 */
function describeError(status: number, body: any) {
  const error = body && body.error;

  if (error && typeof error.message === 'string' && error.message) {
    return error.message;
  }

  switch (status) {
    case 401:
      return 'Mixcloud sign-in has expired.';
    case 403:
      return 'Mixcloud is rate limiting Fadila right now.';
    case 404:
      return 'Mixcloud does not have this any more.';
    default:
      return `Mixcloud returned ${status}.`;
  }
}

async function mixcloudRequest(url: string, skipApiUrl = false): Promise<any> {
  const res = await fetch(skipApiUrl ? url : apiUrl + url, {
    headers: {
      Accept: 'application/json; charset=utf-8',
    },
  });

  const text = await res.text();
  let json: any;

  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }

  if (!res.ok) {
    const retryAfter =
      json && json.error && typeof json.error.retry_after === 'number'
        ? json.error.retry_after
        : undefined;

    throw new MixcloudApiError(describeError(res.status, json), res.status, json, retryAfter);
  }

  return json;
}

function pageLimit(limit?: number) {
  return Math.min(pageLimitMax, Math.max(1, limit || pageLimitMax));
}

/**
 * Wraps one page as a QueryIterator whose `next` fetches `paging.next`.
 * Mixcloud pages a search by `offset` and a user's shows by `until`, but
 * both arrive as a finished URL, so neither is this side's problem.
 */
function pageToIterator<T>(page: MixcloudPage<T>): QueryIterator<T> {
  const data = (page && page.data) || [];
  const next = page && page.paging && page.paging.next;

  return {
    results: data,
    totalResults: undefined,
    next: next
      ? async function nextPage() {
          return pageToIterator<T>(await mixcloudRequest(next, true));
        }
      : undefined,
  };
}

/** Shows matching a query, Mixcloud's own relevance order. */
export async function searchCloudcasts(
  query: string,
  limit?: number,
): Promise<QueryIterator<MixcloudCloudcast>> {
  return pageToIterator<MixcloudCloudcast>(
    await mixcloudRequest(
      `/search/?type=cloudcast&limit=${pageLimit(limit)}&q=${encodeURIComponent(query)}`,
    ),
  );
}

/** Accounts matching a query - DJs, stations, shows. */
export async function searchUsers(
  query: string,
  limit?: number,
): Promise<QueryIterator<MixcloudUser>> {
  return pageToIterator<MixcloudUser>(
    await mixcloudRequest(
      `/search/?type=user&limit=${pageLimit(limit)}&q=${encodeURIComponent(query)}`,
    ),
  );
}

/**
 * What one account has uploaded, newest first.
 *
 * @param userKey The user's path form, `/solomun/`, as `key` gives it.
 */
export async function getUserCloudcasts(
  userKey: string,
  limit?: number,
): Promise<QueryIterator<MixcloudCloudcast>> {
  return pageToIterator<MixcloudCloudcast>(
    await mixcloudRequest(`${userKey}cloudcasts/?limit=${pageLimit(limit)}`),
  );
}

/**
 * What one account has marked as a favourite, newest first.
 *
 * Public, and that is the whole point: Mixcloud's OAuth would have bought
 * favourites and following, and both are readable without it. Only listening
 * history (`/listens/`, which answers empty) and writing anything back are
 * behind a sign-in that does not currently exist - see docs/MIXCLOUD.md
 * section 3.
 */
export async function getUserFavorites(
  userKey: string,
  limit?: number,
): Promise<QueryIterator<MixcloudCloudcast>> {
  return pageToIterator<MixcloudCloudcast>(
    await mixcloudRequest(`${userKey}favorites/?limit=${pageLimit(limit)}`),
  );
}

/** The accounts one account follows. Public, as favourites are. */
export async function getUserFollowing(
  userKey: string,
  limit?: number,
): Promise<QueryIterator<MixcloudUser>> {
  return pageToIterator<MixcloudUser>(
    await mixcloudRequest(`${userKey}following/?limit=${pageLimit(limit)}`),
  );
}

/**
 * An account's playlists - its own ordered sets of shows.
 *
 * Public like the rest: no sign-in to read them, and no way to write one
 * back without OAuth.
 */
export async function getUserPlaylists(
  userKey: string,
  limit?: number,
): Promise<QueryIterator<MixcloudPlaylist>> {
  return pageToIterator<MixcloudPlaylist>(
    await mixcloudRequest(`${userKey}playlists/?limit=${pageLimit(limit)}`),
  );
}

/**
 * The shows in one playlist.
 *
 * @param playlistKey `/spartacus/playlists/jazz/`, as `key` gives it.
 */
export async function getPlaylistShows(
  playlistKey: string,
  limit?: number,
): Promise<QueryIterator<MixcloudCloudcast>> {
  return pageToIterator<MixcloudCloudcast>(
    await mixcloudRequest(`${playlistKey}cloudcasts/?limit=${pageLimit(limit)}`),
  );
}

/**
 * Mixcloud's browse categories - 38 of them, in one page, no paging.
 *
 * Not a QueryIterator for that reason: there is no next, and pretending
 * otherwise would put a load-more on a list that is already complete.
 */
export async function getCategories(): Promise<MixcloudCategory[]> {
  const page: MixcloudPage<MixcloudCategory> = await mixcloudRequest('/categories/');

  return (page && page.data) || [];
}

/**
 * Shows in a category.
 *
 * `/discover/<slug>/popular/` and `/discover/<slug>/latest/` are what is left
 * of discovery on Mixcloud; `hot` and `new` answer 404, and the root
 * `/popular/` is read as a user called "popular".
 */
export async function getCategoryShows(
  slug: string,
  sort: MixcloudDiscoverSort,
  limit?: number,
): Promise<QueryIterator<MixcloudCloudcast>> {
  return pageToIterator<MixcloudCloudcast>(
    await mixcloudRequest(
      `/discover/${encodeURIComponent(slug)}/${sort}/?limit=${pageLimit(limit)}`,
    ),
  );
}

/** One show, by the key that is also its API path. */
export async function getCloudcast(key: string): Promise<MixcloudCloudcast> {
  return await mixcloudRequest(key);
}

/** One user, by the key that is also their API path. */
export async function getUser(key: string): Promise<MixcloudUser> {
  return await mixcloudRequest(key);
}

/** Whether what the user typed into the search box is a mixcloud.com link. */
export function isMixcloudUrl(value: string) {
  return /^(https?:\/\/)?(www\.)?(m\.)?mixcloud\.com\//i.test(value.trim());
}

/**
 * The API resource for a mixcloud.com page. Mixcloud has no /resolve
 * endpoint and does not need one: the path of the web URL *is* the key, so
 * the whole of resolving a pasted link is taking the path and asking for it.
 *
 * Returns a show or a user; the caller tells them apart with isCloudcast.
 * Anything else on the site - a tag page, /discover/ - comes back as
 * whatever the API makes of that path, or throws 404.
 */
export async function resolve(url: string): Promise<MixcloudCloudcast | MixcloudUser> {
  const key = keyFromUrl(url);

  if (!key) {
    throw new MixcloudApiError('That does not look like a Mixcloud link.', 400);
  }

  return await mixcloudRequest(key);
}

/**
 * The key (`/user/slug/`) inside a mixcloud.com URL, or undefined when there
 * is none. Query strings and fragments are dropped - a shared link carries
 * `?utm_source=` more often than not - and a missing trailing slash is added,
 * because the API wants one and people paste without.
 */
export function keyFromUrl(url: string): string | undefined {
  const trimmed = url.trim();

  if (!isMixcloudUrl(trimmed)) {
    return undefined;
  }

  let parsed: URL;

  try {
    parsed = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
  } catch {
    return undefined;
  }

  const path = parsed.pathname;

  if (path === '/' || path === '') {
    return undefined;
  }

  return path.endsWith('/') ? path : `${path}/`;
}
